import React, {useState} from "react";
import {useGuideFlowProviderContext} from "../../../GuideFlowProviderContext";
import {useForm} from "react-hook-form";
import {ErrorMessage, Field, Label} from "../../controls/tailwindui/fieldset";
import {Input} from "../../controls/tailwindui/input";
import {useNavigate} from "react-router-dom";
import toast from "react-hot-toast";
import {Button} from "../../controls/tailwindui/button";
import SpinnerIcon from "../../controls/icons/SpinnerIcon";
import {SECTION_ROOT} from "./FormTypes";
import {Contact} from "../../../providers/contact/ContactProvider";
import {ContactNote} from "../../../providers/contact/ContactNotesProvider";
import moment from "moment/moment";

const emptyContact: Contact = {
    initiations: [],
    id: "",
    firstname: "",
    surname: "",
    email: "",
    phone: "",
    mmsStudentNumber: "",
    dateOfBirth: 0
};


export default function AddNewContactInPersonView() {
    const navigate = useNavigate();
    const context = useGuideFlowProviderContext();
    const {register, reset, handleSubmit, formState: {errors}} = useForm<Contact>({defaultValues: emptyContact});

    const [isSaving, setIsSaving] = useState(false);
    const [newNoteContent, setNewNoteContent] = useState("");

    const handleChange = (event: any) => {
        setNewNoteContent(event.target.value);
    };

    const onSubmit = async (data: Contact) => {
        setIsSaving(true);

        try {
            const id = await context.Contacts.save(data);

            let noteSaved = false;
            if (newNoteContent.trim().length > 0) {
                try {
                    const note: ContactNote = {
                        id: "",
                        contactId: id,
                        content: newNoteContent,
                        timestamp: moment().unix(),
                    };

                    await context.Contacts.Notes.save(note);
                    noteSaved = true;
                } catch (e) {
                    console.error("Failed to save note:", e);
                }
            }

            reset(emptyContact);

            if (noteSaved) {
                toast.success("New contact added with notes", { icon: "🥰" });
            } else if (newNoteContent.trim().length > 0) {
                toast.success("New contact added, but notes could not be saved", {
                    icon: "⚠️",
                });
            } else {
                toast.success("New contact added", { icon: "🥰" });
            }

            navigate(SECTION_ROOT);
        } finally {
            setIsSaving(false);
        }
    };


    return (
        <div className="p-3">
            <div>
                <h2 className="text-2xl font-bold tracking-tight text-gray-900">New Contact</h2>
                <p className="mt-1 text-sm leading-6 text-gray-400">
                    You can add images and other information later.
                </p>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="xl:gap-x-16">
                    <div>
                        <div className="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                            {/*<h2 className="text-lg font-medium text-gray-900">Contact information</h2>*/}
                            <div>
                                <Field>
                                    <Label>First Name</Label>
                                    <Input {...register("firstname", {required: true,})}
                                           invalid={errors?.firstname != null} autoFocus={true}/>
                                    {errors?.firstname && <ErrorMessage>*{errors.firstname.message}</ErrorMessage>}
                                </Field>
                            </div>

                            <div>
                                <Field>
                                    <Label>Surname</Label>
                                    <Input {...register("surname", {required: true,})}
                                           invalid={errors?.surname != null}/>
                                    {errors?.surname && <ErrorMessage>*{errors.surname.message}</ErrorMessage>}
                                </Field>
                            </div>
                            <div>
                                <Field>
                                    <Label>Email</Label>
                                    <Input {...register("email", {required: false,})} invalid={errors?.email != null}/>
                                    {errors?.email && <ErrorMessage>*{errors.email.message}</ErrorMessage>}
                                </Field>
                            </div>
                            <div>
                                <Field>
                                    <Label>Phone</Label>
                                    <Input {...register("phone", {required: false,})} invalid={errors?.phone != null}/>
                                    {errors?.phone && <ErrorMessage>*{errors.phone.message}</ErrorMessage>}
                                </Field>
                            </div>
                            <div>
                                <Field>
                                    <Label>Birth Date</Label>
                                    <Input type="date"
                                           {...register("dateOfBirth", {
                                               required: false,

                                           })}
                                           invalid={errors?.dateOfBirth != null}/>
                                    {errors?.dateOfBirth && <ErrorMessage>*{errors.dateOfBirth.message}</ErrorMessage>}
                                </Field>
                            </div>
                            <div>
                                <Field>
                                    <Label>Student Number</Label>
                                    <Input {...register("mmsStudentNumber", {required: false,})}
                                           invalid={errors?.mmsStudentNumber != null}/>
                                    {errors?.mmsStudentNumber &&
                                        <ErrorMessage>*{errors.mmsStudentNumber.message}</ErrorMessage>}
                                </Field>
                            </div>
                        </div>
                        <div className="mt-6">
                            <Field>
                                <Label>Notes</Label>
                                <textarea
                                    rows={4}
                                    name="comment"
                                    id="comment"
                                    className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 sm:text-sm"
                                    placeholder="Add your notes..."
                                    value={newNoteContent}
                                    onChange={handleChange}
                                />
                            </Field>
                        </div>

                    </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-x-6">
                    <Button type="button" disabled={isSaving} onClick={() => navigate(SECTION_ROOT)} outline={true}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={isSaving}
                        color="indigo">
                        {isSaving && <SpinnerIcon/>}
                        Save
                    </Button>
                </div>
            </form>
        </div>
    )
}
